import __ from "src/utils/Translations";
import React, { FC } from "react";
import SecureRoute from "src/utils/RoleBasedSecurity/SecurityComponents/SecureRoute";
import OrganizationStructure from "src/Pages/Company/OrganizationStructure";
import CreateStructure from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure";
import ModuleSettings from "src/Pages/Company/OrganizationStructure/ModuleSettings";
import Localizations from "src/Pages/Company/OrganizationStructure/Localizations";
import Positions from "src/Pages/Company/OrganizationStructure/Positions";
import {
  organizationStructureStructureCreate,
  organizationStructureModuleRead,
} from "src/utils/RoleBasedSecurity/permissions";
import OrganizationStructureProviders from "src/Pages/Company/OrganizationStructure/Providers/OrganizationStructureProviders";
import { match, Route, Switch } from "react-router-dom";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";
import { useCompanyName } from "src/Pages/Company/CompanyContext";
import { Loader } from "src/Components/Loader/Loader";
import TilesMenu from "src/Components/TilesMenu";
import { AhrMainNav } from "src/Layout/AppNav/NavItems";
import ImportExportAreaData from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/ImportExportAreaData";
import { useHasStructure } from "src/Pages/Company/OrganizationStructure/Hooks/useHasStructure";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import ImportExportLocalizations from "src/Pages/Company/OrganizationStructure/Localizations/ImportExportLocalizations";
import HRToolsErrorBoundary from "src/Components/Error/ErrorBoundary";
import Page404 from "src/utils/ErrorPages/Page404/Page404";
import ImportExportPositions from "src/Pages/Company/OrganizationStructure/Positions/ImportExportPositions";

interface IOrganizationStructureRoutesAhr {
  match: match;
}

const OrganizationStructureRoutesAhr: FC<IOrganizationStructureRoutesAhr> = ({
  match,
}) => {
  const customConfig =
    AhrMainNav.find((el) => el.id === "organization_structure")?.content ?? [];
  const companyName = useCompanyName();
  const hasLocalizationsEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_LOCALIZATIONS
  );
  const hasLocalizationsExportEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_LOCALIZATIONS_EXPORT
  );
  const hasLocalizationsImportEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_LOCALIZATIONS_IMPORT
  );
  const hasPositionsEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_POSITIONS
  );
  const hasPositionsExportEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_POSITIONS_EXPORT
  );
  const hasPositionsImportEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_POSITIONS_IMPORT
  );

  const hasImportAreasEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_IMPORT_AREAS
  );

  const hasExportAreasEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_IMPORT_AREAS
  );

  const { structureId } = useStructureStore();
  const { hasStructure, isLoading } = useHasStructure();

  if (isLoading) {
    return <Loader />;
  }

  return (
    <HRToolsErrorBoundary module="OrganizationStructure">
      <OrganizationStructureProviders>
        <Switch>
          <SecureRoute
            permission={organizationStructureModuleRead}
            exact
            path={`${match.url}/organization-structure`}
            render={() => (
              <TilesMenu
                config={customConfig}
                title={__("Struktura Organizacji")}
                breadcrumbs={[
                  {
                    title: `${__("Zarządzanie firmą")} ${companyName}`,
                    link: `${match.url}`,
                  },
                ]}
                hasStructure={hasStructure}
              />
            )}
          />
          <SecureRoute
            exact
            permission={organizationStructureStructureCreate}
            path={`${match.url}/organization-structure/create-structure`}
            component={CreateStructure}
          />
          <SecureRoute
            permission={organizationStructureModuleRead}
            path={`${match.url}/organization-structure/configure-structure`}
            component={OrganizationStructure}
          />
          <SecureRoute
            exact
            permission={organizationStructureModuleRead}
            path={`${match.url}/organization-structure/module-settings`}
            component={ModuleSettings}
          />
          {hasLocalizationsEnabled && hasStructure ? (
            <SecureRoute
              exact
              permission={organizationStructureModuleRead}
              path={`${match.url}/organization-structure/localizations`}
              component={Localizations}
            />
          ) : null}
          {hasLocalizationsExportEnabled || hasLocalizationsImportEnabled ? (
            <SecureRoute
              permission={organizationStructureModuleRead}
              path={`${match.url}/organization-structure/localizations/import-export-localizations`}
              component={ImportExportLocalizations}
            />
          ) : null}
          {hasPositionsEnabled && hasStructure ? (
            <SecureRoute
              permission={organizationStructureModuleRead}
              path={`${match.url}/organization-structure/positions`}
              component={Positions}
            />
          ) : null}
          {hasPositionsExportEnabled || hasPositionsImportEnabled ? (
            <SecureRoute
              permission={organizationStructureModuleRead}
              path={`${match.url}/organization-structure/positions/import-export-positions`}
              component={ImportExportPositions}
            />
          ) : null}
          {hasStructure && (hasImportAreasEnabled || hasExportAreasEnabled) ? (
            <SecureRoute
              exact
              permission={organizationStructureModuleRead}
              path={`${match.url}/organization-structure/import-export-area-data`}
              render={() => <ImportExportAreaData structureId={structureId!} />}
            />
          ) : null}
          <Route>
            <Page404 />
          </Route>
        </Switch>
      </OrganizationStructureProviders>
    </HRToolsErrorBoundary>
  );
};
export default OrganizationStructureRoutesAhr;
